import { Suspense } from 'react';
import { ProjectEntityDescription } from '@gonfalon/chrome/project-entity-layout';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { DateFormat } from '@gonfalon/format/constants';
import { TextTruncatorWithTooltip } from '@gonfalon/launchpad-experimental/TextTruncatorWithTooltip';
import { MemberAvatar } from '@gonfalon/members/MemberAvatar';
import { memberInitials } from '@gonfalon/members/memberInitials';
import { toViewDetail } from '@gonfalon/navigator/navigators';
import { getViewQuery } from '@gonfalon/rest-api/queries/views';
import {
  IconButton,
  LinkButton,
  ProgressBar,
  Tag,
  TagGroup,
  TagList,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useSuspenseQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import type { View } from '../types';

import styles from './ViewDetailsPanel.module.css';

const VIEW_DETAIL_STALE_TIME = 5 * 60 * 1000;

interface ViewDetailsPanelProps {
  view: View;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  canToggleFavorites: boolean;
  projectKey: string;
}

function ViewDetailsPanelInternal({
  view,
  isFavorite,
  onToggleFavorite,
  canToggleFavorites,
  projectKey,
}: ViewDetailsPanelProps) {
  const { data: fullView } = useSuspenseQuery({
    ...getViewQuery({
      projectKey,
      viewKey: view.key,
      query: { expand: ['maintainer', 'resourceSummary'] },
      header: { 'LD-API-Version': 'beta' },
    }),
    staleTime: VIEW_DETAIL_STALE_TIME,
  });

  const path = toViewDetail({ projectKey, viewKey: view.key });
  const description = fullView.description?.trim();

  return (
    <div className={styles.detailsPanel}>
      <div className={styles.detailHeader}>
        <TextTruncatorWithTooltip tooltipContent={fullView.name} className={styles.viewName} maxLines={2}>
          {fullView.name}
        </TextTruncatorWithTooltip>
        <TooltipTrigger>
          <IconButton
            aria-label={isFavorite ? `Remove ${view.name} from favorites` : `Add ${view.name} to favorites`}
            icon={isFavorite ? 'star' : 'star-outline'}
            size="small"
            variant="minimal"
            className={`${styles.favoriteButton} ${isFavorite ? styles.favoriteButtonActive : ''}`.trim()}
            onPress={onToggleFavorite}
            isDisabled={!canToggleFavorites}
          />
          <Tooltip>{isFavorite ? 'Remove from favorites' : 'Add to favorites'}</Tooltip>
        </TooltipTrigger>
      </div>

      {description && (
        <div className={styles.description}>
          <ProjectEntityDescription text={description} />
        </div>
      )}

      {fullView.tags.length > 0 && (
        <TagGroup aria-label="View tags">
          <TagList>
            {fullView.tags.map((tag) => (
              <Tag key={tag} size="small">
                {tag}
              </Tag>
            ))}
          </TagList>
        </TagGroup>
      )}

      {fullView.resourceSummary && (
        <div className={styles.resourceCounts}>
          <div className={styles.countCell}>
            <span className={styles.countLabel}>
              <Icon name="flag-outline" size="tiny" /> Flags
            </span>
            <span className={styles.countValue}>{fullView.resourceSummary.flagCount}</span>
          </div>
          <div className={styles.countCell}>
            <span className={styles.countLabel}>
              <Icon name="chart-venn" size="tiny" /> Segments
            </span>
            <span className={styles.countValue}>{fullView.resourceSummary.segmentCount ?? 0}</span>
          </div>
          <div className={styles.countCell}>
            <span className={styles.countLabel}>
              <Icon name="hub" size="tiny" /> Configs
            </span>
            <span className={styles.countValue}>{fullView.resourceSummary.aiConfigCount ?? 0}</span>
          </div>
        </div>
      )}

      {fullView.maintainer && (
        <div className={styles.section}>
          <h4 className={styles.sectionHeader}>Maintainer</h4>
          <div className={styles.maintainerRow}>
            {fullView.maintainer.maintainerMember && (
              <>
                <MemberAvatar
                  size="small"
                  email={fullView.maintainer.maintainerMember.email}
                  initials={memberInitials(fullView.maintainer.maintainerMember)}
                />
                <span>
                  {[fullView.maintainer.maintainerMember.firstName, fullView.maintainer.maintainerMember.lastName]
                    .filter(Boolean)
                    .join(' ') || fullView.maintainer.maintainerMember.email}
                </span>
              </>
            )}
            {fullView.maintainer.maintainerTeam && (
              <>
                <Icon name="group" size="small" />
                <span>{fullView.maintainer.maintainerTeam.name}</span>
              </>
            )}
          </div>
        </div>
      )}

      <div className={styles.section}>
        <h4 className={styles.sectionHeader}>Created</h4>
        <span className={styles.sectionContentPrimary}>{format(fullView.createdAt, DateFormat.MMM_YYYY)}</span>
      </div>

      <div className={styles.linkButtonWrapper}>
        <TooltipTrigger>
          <LinkButton href={path} target="_blank" className={styles.linkButton} aria-label="See view details">
            <Icon name="link-external" size="small" />
          </LinkButton>
          <Tooltip>See view details</Tooltip>
        </TooltipTrigger>
      </div>
    </div>
  );
}

export function ViewDetailsPanel(props: ViewDetailsPanelProps) {
  return (
    <ErrorBoundary
      severity="medium"
      fallbackRender={() => (
        <div className={styles.errorContainer}>
          <span className={styles.errorMessage}>
            <Icon name="warning" className={styles.errorIcon} />
            Failed to load view details
          </span>
        </div>
      )}
    >
      <Suspense
        fallback={
          <div className={styles.loadingContainer}>
            <ProgressBar size="small" isIndeterminate aria-label="Loading view details" />
          </div>
        }
      >
        <ViewDetailsPanelInternal {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}
